/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { DexterModal } from "../../Shared/DexterModal";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import {
  replaceExtension,
  TYPE_OPTIONS,
  useListMaxHeight,
} from "../../Utils/Common";
import {
  RemoveFileFn,
  UploadedFile,
  UploadFileFn,
  UploadModal,
} from "../../Shared/Upload/UploadModal";
import { Button, KIND, SIZE } from "baseui/button";
import { Block } from "baseui/block";
import { handleApolloError } from "../../Shared/Errors";
import { ApolloError, gql, useApolloClient, useMutation } from "@apollo/client";
import { FileInfo } from "Api";
import updateFileGql from "../../Api/Gql/UpdateFile";
import GetFile from "../../Api/Gql/GetFile";
import { ReviewList } from "./ReviewList";
import { FaCheck } from "react-icons/fa";
import { VerifyFileTable } from "./VerifyFileTable";

const GET_FILE = gql(GetFile);
const UPDATE_FILE = gql(updateFileGql);

type PopulateStep = 1 | 2 | 3;

const STEPS = [
  { label: "Upload" },
  { label: "Verify file types" },
  { label: "Review" },
];

const CONFIRM_LABELS: Record<PopulateStep, string> = {
  1: "Next: Verify file types",
  2: "Next: Review",
  3: "Add files to Mapping",
};

type FileType = FileInfo["userSelectedFileType"];

const UPLOAD_STEP_RESERVED_HEIGHT = 720;
// Minimum height for upload list.
const MIN_LIST_HEIGHT = 300;

// ── Stepper ──────────────────────────────────────────────────────────────────

const CIRCLE_SIZE = 44;

const PopulateStepper = ({
  currentStep,
}: {
  currentStep: PopulateStep;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const dark = currentTheme.colors.defaultTextColor;
  const light = currentTheme.colors.pageBackgroundColor;
  const muted = currentTheme.colors.secondaryTextColor;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        padding: `${currentTheme.spacing.sm} 0 ${currentTheme.spacing.lg}`,
        boxSizing: "border-box",
      }}
    >
      {STEPS.map((step, index) => {
        const number = index + 1;
        const isComplete = number < currentStep;
        const isCurrent = number === currentStep;
        const reached = isComplete || isCurrent;

        return (
          <React.Fragment key={step.label}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                flex: "0 0 auto",
                minWidth: 96,
              }}
            >
              <div
                style={{
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: "50%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 6,
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: isComplete ? 26 : 15,
                  fontWeight: isComplete ? 700 : 500,
                  background: reached
                    ? dark
                    : currentTheme.colors.tabHoverColor,
                  color: reached ? light : muted,
                  // Current step: white inner ring + dark outer ring.
                  border: isCurrent ? `2px solid ${light}` : "none",
                  boxShadow: isCurrent ? `0 0 0 2px ${dark}` : "none",
                }}
              >
                {isComplete ? <FaCheck /> : number}
              </div>
              <span
                style={{
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: currentTheme.fontSizes.small,
                  fontWeight: reached ? 600 : 400,
                  color: reached ? dark : muted,
                }}
              >
                Step {number}
              </span>
              {reached && (
                <span
                  style={{
                    fontFamily: currentTheme.fonts.primary,
                    fontSize: currentTheme.fontSizes.small,
                    color: dark,
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.label}
                </span>
              )}
            </div>

            {index < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  marginTop: CIRCLE_SIZE / 2 - 1,
                  marginLeft: -26,
                  marginRight: -26,
                  background: dark,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────

interface PopulateModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadFile: UploadFileFn;
  removeFile: RemoveFileFn;
  onError?: (message: string) => void;
  /** Called on "Add files to Mapping" with each file and its confirmed type. */
  onNext: (files: FileInfo[]) => void;
}

export const PopulateModal = ({
  isOpen,
  onClose,
  uploadFile,
  removeFile,
  onError,
  onNext,
}: PopulateModalProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const apolloClient = useApolloClient();
  const [updateFileMutation] = useMutation(UPDATE_FILE);

  const [step, setStep] = React.useState<PopulateStep>(1);

  // Live view of what the embedded uploader has uploaded so far (step 1).
  const [pendingFiles, setPendingFiles] = React.useState<UploadedFile[]>([]);
  const [canCommit, setCanCommit] = React.useState(false);

  // Server records for the files confirmed with Next on step 1.
  const [files, setFiles] = React.useState<FileInfo[]>([]);
  const [loadingFiles, setLoadingFiles] = React.useState(false);

  // Latest values for the close effect.
  const stepRef = React.useRef(step);
  stepRef.current = step;
  const filesRef = React.useRef(files);
  filesRef.current = files;
  const pendingRef = React.useRef(pendingFiles);
  pendingRef.current = pendingFiles;
  const finishedRef = React.useRef(false);

  const uploadListMaxHeight = useListMaxHeight(
    UPLOAD_STEP_RESERVED_HEIGHT,
    MIN_LIST_HEIGHT
  );

  React.useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPendingFiles([]);
      setCanCommit(false);
      setFiles([]);
      setLoadingFiles(false);
      finishedRef.current = false;
      return;
    }
    // Closed without "Add files to Mapping": remove what was uploaded.
    // On step 1 the embedded uploader does this itself (its rows include any
    // restored files), so only handle steps 2 and 3 here. FileInfo has no
    // pdfParentId, so take it from the upload record.
    if (!finishedRef.current && stepRef.current !== 1) {
      const ids = new Set<string>();
      filesRef.current.forEach((file) => {
        ids.add(file.id);
        const parentId = pendingRef.current.find(
          (upload) => upload.id === file.id
        )?.pdfParentId;
        if (parentId) ids.add(parentId);
      });
      ids.forEach((id) => {
        removeFile(id).catch(() => {
          // Best-effort rollback.
        });
      });
    }
  }, [isOpen, removeFile]);

  // Stable so the uploader's onChange effect doesn't loop.
  const handleUploadChange = React.useCallback(
    (uploaded: UploadedFile[], ready: boolean) => {
      setPendingFiles(uploaded);
      setCanCommit(ready);
    },
    []
  );

  // Uploads to restore in the uploader after Back: only the files still in
  // the list, under their current (possibly renamed) names.
  const initialUploads = React.useMemo(
    () =>
      pendingFiles.flatMap((upload) => {
        const info = files.find((file) => file.id === upload.id);
        return info ? [{ ...upload, name: info.attributes?.name }] : [];
      }),
    [pendingFiles, files]
  );

  const fetchFileInfos = React.useCallback(
    async (uploads: UploadedFile[]): Promise<FileInfo[]> => {
      const results = await Promise.all(
        uploads.map((upload) =>
          apolloClient.query({
            query: GET_FILE,
            variables: { id: upload.id },
            fetchPolicy: "network-only",
          })
        )
      );
      return results
        .map((result) => result.data?.getFile as FileInfo | undefined)
        .filter((info): info is FileInfo => Boolean(info))
        .map((info) => ({
          ...info,
          // A type saved earlier (e.g. before Back) wins over the guess.
          userSelectedFileType: info.userSelectedFileType,
        }));
    },
    [apolloClient]
  );

  const handleTypeChange = React.useCallback(
    async (fileId: string, fileType: string): Promise<void> => {
      const current = files.find((file) => file.id === fileId);
      if (!current || current.userSelectedFileType === fileType) return;

      // Same rename as the Files page: the extension follows the new type.
      const extension =
        TYPE_OPTIONS.find((option) => option.id === fileType)?.extension ?? "";

      const newName = extension
        ? replaceExtension(current.attributes?.name, extension)
        : current.attributes?.name;

      const userSelectedFileType = fileType as FileType;

      setFiles((prev) =>
        prev.map((file) =>
          file.id === fileId
            ? {
                ...file,
                userSelectedFileType,
                attributes: { ...file.attributes, name: newName },
              }
            : file
        )
      );

      try {
        await updateFileMutation({
          variables: {
            fileInfo: {
              id: fileId,
              userSelectedFileType,
              securityMarkings: current.securityMarkings,
              ...(newName !== current.attributes?.name
                ? { name: newName }
                : {}),
            },
          },
        });
      } catch (error) {
        setFiles((prev) =>
          prev.map((file) => (file.id === fileId ? current : file))
        );
        handleApolloError(
          error as ApolloError,
          `Couldn't change the file type of ${current.attributes?.name}.`
        );
      }
    },
    [files, updateFileMutation]
  );

  const handleRemove = (fileId: string): void => {
    removeFile(fileId).catch(() => {
      // Best-effort; the row is removed from the list regardless.
    });
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleConfirm = async (): Promise<void> => {
    if (step === 1) {
      setLoadingFiles(true);
      try {
        setFiles(await fetchFileInfos(pendingFiles));
        setStep(2);
      } catch (error) {
        handleApolloError(
          error as ApolloError,
          "Couldn't load the uploaded files."
        );
      } finally {
        setLoadingFiles(false);
      }
    } else if (step === 2) {
      setStep(3);
    } else {
      finishedRef.current = true;
      onNext(files);
    }
  };

  const handleBack = (): void => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  return (
    <DexterModal
      customWidth={750}
      isOpen={isOpen}
      title="Add files to Mapping"
      confirmLabel={CONFIRM_LABELS[step]}
      confirmDisabled={
        loadingFiles ||
        (step === 1 && !canCommit) ||
        (step !== 1 && files.length === 0)
      }
      onConfirm={handleConfirm}
      onCancel={onClose}
      footerStart={
        step !== 1 ? (
          <Button
            kind={KIND.tertiary}
            size={SIZE.default}
            onClick={handleBack}
            overrides={{
              BaseButton: {
                style: {
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: currentTheme.fontSizes.medium,
                  backgroundColor: "transparent",
                  color: currentTheme.colors.text,
                },
              },
            }}
          >
            Back
          </Button>
        ) : undefined
      }
    >
      <Block width="100%" maxWidth="520px" marginLeft="auto" marginRight="auto">
        <PopulateStepper currentStep={step} />
      </Block>

      {step === 1 && (
        <UploadModal
          embedded
          compactWhenFilesAdded
          listMaxHeight={uploadListMaxHeight}
          isOpen={isOpen}
          onClose={onClose}
          uploadFile={uploadFile}
          removeFile={removeFile}
          onError={onError}
          onChange={handleUploadChange}
          initialFiles={initialUploads}
        />
      )}

      {step === 2 && (
        <VerifyFileTable
          files={files}
          onTypeChange={handleTypeChange}
          onRemove={handleRemove}
        />
      )}

      {step === 3 && <ReviewList files={files} />}
    </DexterModal>
  );
};
